import {
  ApiError,
  createCustomerSchema,
  formatUsPhone,
  MAX_SMS_MEDIA_BYTES,
  MAX_SMS_MEDIA_COUNT,
  queryKeys,
  SMS_MEDIA_CONTENT_TYPES,
  splitSmsCompliance,
} from '@mevaser/shared'
import {
  Alert,
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Field,
  humanizeStatus,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Skeleton,
  SUPPORT_CHANNEL_TONE,
  SUPPORT_TICKET_STATUS_TONE,
  Textarea,
  useToast,
} from '@mevaser/ui'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Link, useParams } from '@tanstack/react-router'
import {
  ArrowLeft,
  ArrowLeftRight,
  Bot,
  Flag,
  MessageSquare,
  MessageSquareOff,
  Paperclip,
  Pencil,
  Phone,
  PhoneOutgoing,
  RotateCcw,
  RotateCw,
  StickyNote,
  UserMinus,
  UserPlus,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { AiAssistButton } from '../components/ai-assist.js'
import { Page, PageHeader } from '../components/app-shell.js'
import {
  AttachButton,
  AttachmentChips,
  AttachmentList,
  useFileUploads,
} from '../components/attachments.js'
import { LinkifiedText } from '../components/linkified-text.js'
import { MarkImportantButton } from '../components/mark-important.js'
import { CallButton } from '../components/softphone/call-button.js'
import { NewTaskDialog } from '../components/task-shared.js'
import { TicketCameraMedia } from '../components/ticket-camera-media.js'
import { TicketFollowUps } from '../components/ticket-follow-ups.js'
import { api, apiBaseUrl } from '../lib/api.js'
import { useAuth } from '../lib/auth.js'

export interface SupportTicketDetail {
  id: string
  ticketNumber: string
  subject: string
  channel: string
  status: string
  contactPhone: string | null
  contactEmail: string | null
  customerId: string | null
  customerName: string | null
  assignedStaffId: string | null
  assignedStaffName: string | null
  urgent: boolean
  chatActive: boolean
  lastMessageAt: string
  createdAt: string
}

/** A picture on a text (t1231). */
interface TicketAttachment {
  id: string
  filename: string
  contentType: string
  sizeBytes: number
}

export interface SupportTicketMessage {
  id: string
  ticketId: string
  type: string
  body: string
  attachments?: TicketAttachment[]
  fromNumber: string | null
  toNumber: string | null
  staffId: string | null
  staffName: string | null
  hasRecording: boolean
  recordingDurationSeconds: number | null
  transcriptText: string | null
  transcriptStatus: 'pending' | 'completed' | 'failed' | 'skipped' | null
  aiCall: {
    status: 'active' | 'voicemail' | 'resolved' | 'abandoned'
    collected: { name: string | null; callbackNumber: string | null; reason: string | null } | null
    transcript: { role: 'caller' | 'ai'; text: string }[]
  } | null
  autoReply?: 'after_hours' | 'faq' | null
  emailStatus?: 'queued' | 'sent' | 'failed' | null
  emailError?: string | null
  createdAt: string
}

/** Earlier tickets from the same phone number (t1230). */
interface SupportTicketEarlier {
  previous: { ticket: SupportTicketDetail; messages: SupportTicketMessage[] } | null
  others: SupportTicketDetail[]
}

interface SupportTicketEvent {
  id: string
  fromStatus: string | null
  toStatus: string
  actorType: string
  actorLabel: string | null
  note: string | null
  createdAt: string
}

interface StaffRow {
  id: string
  name: string
  isActive: boolean
}

interface CustomerMatch {
  id: string
  name: string
  phone: string | null
}

interface CannedResponse {
  id: string
  title: string
  body: string
}

const ACTION_LABEL: Record<string, string> = {
  open: 'Reopen',
  pending: 'Waiting on customer',
  resolved: 'Mark resolved',
  closed: 'Close',
}

/** The shop's one-tap close notes come from Settings → Support (t1347); this one is built in because it also adds a task. */
const FOLLOW_UP_NOTE = 'Follow up'

const UNASSIGNED = '__unassigned__'

/** The ticket's linked customer, pre-filled on its follow-ups (t1369). */
function ticketCustomer(ticket: {
  customerId: string | null
  customerName: string | null
}): { id: string; name: string } | undefined {
  return ticket.customerId
    ? { id: ticket.customerId, name: ticket.customerName ?? 'Customer' }
    : undefined
}

/**
 * One support ticket: its conversation thread, its status history, and who
 * it's assigned to. Same shape as the repair detail page — legal moves come
 * from the server, not a list kept here.
 */
export function SupportTicketDetailPage() {
  const { ticketId } = useParams({ from: '/authed/support-tickets/$ticketId' })
  const { can } = useAuth()
  const [moving, setMoving] = useState<string | null>(null)
  const [editingSubject, setEditingSubject] = useState(false)
  const [endingChat, setEndingChat] = useState(false)
  const [followUpNote, setFollowUpNote] = useState<string | null>(null)
  const toast = useToast()
  const queryClient = useQueryClient()

  const { data: ticket, isLoading } = useQuery({
    queryKey: queryKeys.supportTickets.detail(ticketId),
    queryFn: () => api.get<SupportTicketDetail>(`/support-tickets/${ticketId}`),
  })

  const { data: transitions } = useQuery({
    queryKey: queryKeys.supportTickets.transitions(ticketId),
    queryFn: () =>
      api.get<{ from: string; to: string[] }>(`/support-tickets/${ticketId}/transitions`),
    enabled: Boolean(ticket),
  })

  const { data: events } = useQuery({
    queryKey: queryKeys.supportTickets.events(ticketId),
    queryFn: () => api.get<SupportTicketEvent[]>(`/support-tickets/${ticketId}/events`),
    enabled: Boolean(ticket),
  })

  const { data: messages } = useQuery({
    queryKey: queryKeys.supportTickets.messages(ticketId),
    queryFn: () => api.get<SupportTicketMessage[]>(`/support-tickets/${ticketId}/messages`),
    enabled: Boolean(ticket),
    refetchInterval: 15_000,
  })

  const { data: earlier } = useQuery({
    queryKey: queryKeys.supportTickets.earlier(ticketId),
    queryFn: () => api.get<SupportTicketEarlier>(`/support-tickets/${ticketId}/earlier`),
    enabled: Boolean(ticket?.contactPhone),
  })

  const customerNumbers = useCustomerNumbers(
    ticket ?? ({ customerId: null, contactPhone: null } as SupportTicketDetail),
  )

  const toggleUrgent = useMutation({
    mutationFn: (urgent: boolean) => api.patch(`/support-tickets/${ticketId}`, { urgent }),
    onSuccess: async (_d, urgent) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success(urgent ? 'Marked urgent' : 'Urgent mark removed')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  if (isLoading || !ticket) {
    return (
      <Page>
        <Skeleton className="h-8 w-64" />
        <Skeleton className="mt-4 h-64 w-full" />
      </Page>
    )
  }

  const moves = can('support:write') ? (transitions?.to ?? []) : []

  return (
    <Page>
      <div className="pb-3">
        <Link
          to="/support-tickets"
          className="inline-flex items-center gap-1.5 text-sm text-foreground-muted hover:text-foreground"
        >
          <ArrowLeft className="size-3.5" aria-hidden="true" /> All tickets
        </Link>
      </div>

      <PageHeader
        title={ticket.ticketNumber}
        description={`${ticket.subject}${ticket.contactPhone ? ` · ${formatUsPhone(ticket.contactPhone)}` : ''}`}
        actions={
          <div className="flex flex-wrap items-center gap-2">
            <MarkImportantButton itemType="support_ticket" itemId={ticketId} />
            {can('support:write') ? (
              <Button size="sm" variant="ghost" onClick={() => setEditingSubject(true)}>
                <Pencil className="size-3.5" aria-hidden="true" /> Edit subject
              </Button>
            ) : null}
            {ticket.contactPhone ? <CallButton phone={ticket.contactPhone} label="Call" /> : null}
            {!ticket.contactPhone ? (
              <UseCustomerNumber ticket={ticket} numbers={customerNumbers} />
            ) : null}
            {ticket.urgent ? (
              <Badge tone="danger">
                <Flag className="me-0.5 size-3" aria-hidden="true" /> Urgent
              </Badge>
            ) : null}
            {can('support:write') ? (
              <Button
                size="sm"
                variant="ghost"
                disabled={toggleUrgent.isPending}
                onClick={() => toggleUrgent.mutate(!ticket.urgent)}
              >
                <Flag className="size-3.5" aria-hidden="true" />{' '}
                {ticket.urgent ? 'Remove urgent' : 'Mark urgent'}
              </Button>
            ) : null}
            <Badge tone={SUPPORT_TICKET_STATUS_TONE[ticket.status] ?? 'neutral'}>
              {humanizeStatus(ticket.status)}
            </Badge>
            {can('support:write') && ticket.channel === 'web_chat' && ticket.chatActive ? (
              <Button size="sm" variant="ghost" onClick={() => setEndingChat(true)}>
                <MessageSquareOff className="size-3.5" aria-hidden="true" /> End chat
              </Button>
            ) : null}
            {moves.map((to) => (
              <Button
                key={to}
                size="sm"
                variant={to === 'closed' ? 'ghost' : 'primary'}
                onClick={() => setMoving(to)}
              >
                {ACTION_LABEL[to] ?? humanizeStatus(to)}
              </Button>
            ))}
          </div>
        }
      />

      <div className="grid gap-4 lg:grid-cols-[minmax(0,2fr)_minmax(0,1fr)]">
        <div className="space-y-4">
          <ConversationCard
            ticket={ticket}
            messages={messages ?? []}
            earlier={earlier ?? null}
            canReply={can('support:write')}
          />
        </div>

        <div className="space-y-4">
          <DetailsCard ticket={ticket} />
          <TicketCameraMedia ticketId={ticketId} />
          <TicketFollowUps ticketId={ticketId} customer={ticketCustomer(ticket)} />
          <Card>
            <CardHeader>
              <CardTitle>History</CardTitle>
            </CardHeader>
            <CardContent>
              <ol className="space-y-3">
                {(events ?? []).map((event) => (
                  <li key={event.id} className="flex gap-3 text-sm">
                    <span
                      className="mt-1.5 size-1.5 shrink-0 rounded-full bg-border-strong"
                      aria-hidden="true"
                    />
                    <div className="min-w-0">
                      <p>
                        {event.fromStatus ? (
                          <>
                            <span className="text-foreground-muted">
                              {humanizeStatus(event.fromStatus)}
                            </span>{' '}
                            →{' '}
                          </>
                        ) : null}
                        <span className="font-medium">{humanizeStatus(event.toStatus)}</span>
                      </p>
                      <p className="text-2xs text-foreground-muted">
                        {event.actorLabel ?? event.actorType} ·{' '}
                        {new Date(event.createdAt).toLocaleString('en-US', {
                          dateStyle: 'medium',
                          timeStyle: 'short',
                        })}
                      </p>
                      {event.note ? (
                        <p className="mt-0.5 text-xs text-foreground-muted">{event.note}</p>
                      ) : null}
                    </div>
                  </li>
                ))}
              </ol>
            </CardContent>
          </Card>
        </div>
      </div>

      <TransitionDialog
        ticket={ticket}
        to={moving}
        onClose={() => setMoving(null)}
        onFollowUp={(note) => {
          setMoving(null)
          setFollowUpNote(note)
        }}
      />
      <NewTaskDialog
        open={followUpNote !== null}
        onOpenChange={(open) => !open && setFollowUpNote(null)}
        supportTicketId={ticket.id}
        customer={ticketCustomer(ticket)}
        defaultTitle={`Follow up: ${ticket.subject}`.slice(0, 200)}
        onCreated={async (task) => {
          try {
            await api.post(`/support-tickets/${ticket.id}/transition`, {
              to: 'closed',
              note: `${followUpNote ?? FOLLOW_UP_NOTE} — ${task.taskNumber}`.slice(0, 500),
            })
            await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
            toast.success(`${ticket.ticketNumber} closed, follow-up ${task.taskNumber} added`)
          } catch (error) {
            toast.error(
              'Follow-up added, but the ticket could not be closed',
              error instanceof ApiError ? error.message : undefined,
            )
          }
        }}
      />
      <EndChatDialog ticket={ticket} open={endingChat} onClose={() => setEndingChat(false)} />
      <EditSubjectDialog
        ticket={ticket}
        open={editingSubject}
        onClose={() => setEditingSubject(false)}
      />
    </Page>
  )
}

/** A sent text with the auto-added brand and opt-out wording shown small and muted (t1234). */
function SmsOutboundBody({ body }: { body: string }) {
  const { brand, text, optOut } = splitSmsCompliance(body)
  return (
    <p className="whitespace-pre-wrap">
      {brand ? <span className="text-2xs text-foreground-muted">{brand} </span> : null}
      <LinkifiedText text={text} />
      {optOut ? <span className="ms-1.5 text-2xs text-foreground-muted">{optOut}</span> : null}
    </p>
  )
}

export function MessageBubble({
  ticketId,
  message,
}: {
  ticketId: string
  message: SupportTicketMessage
}) {
  const time = new Date(message.createdAt).toLocaleString('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  })

  if (message.type === 'note') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-dashed border-border bg-surface-sunken px-3 py-2 text-sm">
        <StickyNote
          className="mt-0.5 size-3.5 shrink-0 text-foreground-subtle"
          aria-hidden="true"
        />
        <div className="min-w-0">
          {message.body ? (
            <p className="whitespace-pre-wrap">
              <LinkifiedText text={message.body} />
            </p>
          ) : null}
          <AttachmentList
            compact
            files={message.attachments?.map((a) => ({
              ...a,
              url: `${apiBaseUrl}/support-tickets/${ticketId}/attachments/${a.id}`,
            }))}
          />
          <p className="mt-0.5 text-2xs text-foreground-muted">
            {message.staffName ?? 'Staff'} · {time}
          </p>
        </div>
      </div>
    )
  }

  if (message.type === 'call_inbound' || message.type === 'call_outbound') {
    return <CallBubble ticketId={ticketId} message={message} time={time} />
  }

  if (message.type === 'voicemail') {
    return (
      <div className="max-w-[80%] rounded-lg border border-border bg-surface px-3 py-2 text-sm">
        <p className="mb-1.5 font-medium">
          Voicemail
          {message.recordingDurationSeconds ? ` · ${message.recordingDurationSeconds}s` : ''}
        </p>
        {message.hasRecording ? (
          <RecordingPlayer
            src={`${apiBaseUrl}/support-tickets/${ticketId}/messages/${message.id}/recording`}
          />
        ) : null}
        <p className="mt-1 text-2xs text-foreground-muted">{time}</p>
      </div>
    )
  }

  if (message.type === 'ai_receptionist') {
    return <AiReceptionistBubble message={message} time={time} />
  }

  if (message.type === 'chat_ai') {
    return <ChatTranscriptBubble message={message} time={time} />
  }

  const outbound =
    message.type === 'sms_outbound' ||
    message.type === 'chat_outbound' ||
    message.type === 'email_outbound'
  return (
    <div className={`flex ${outbound ? 'justify-end' : 'justify-start'}`}>
      <div
        className={`max-w-[80%] rounded-lg px-3 py-2 text-sm ${
          outbound ? 'bg-primary-subtle text-primary' : 'bg-surface-sunken'
        }`}
      >
        {message.attachments?.length ? (
          <div className="mb-1 flex flex-wrap gap-1">
            {message.attachments.map((a) => (
              <a
                key={a.id}
                href={`${apiBaseUrl}/support-tickets/${ticketId}/attachments/${a.id}`}
                target="_blank"
                rel="noreferrer"
              >
                <img
                  src={`${apiBaseUrl}/support-tickets/${ticketId}/attachments/${a.id}`}
                  alt={a.filename}
                  className="max-h-48 max-w-full rounded-md object-cover"
                />
              </a>
            ))}
          </div>
        ) : null}
        {message.body ? (
          message.type === 'sms_outbound' ? (
            <SmsOutboundBody body={message.body} />
          ) : (
            <p className="whitespace-pre-wrap">
              <LinkifiedText text={message.body} />
            </p>
          )
        ) : null}
        <p className="mt-1 text-2xs text-foreground-muted">
          {outbound
            ? message.autoReply === 'faq'
              ? 'Automatic answer'
              : message.autoReply === 'after_hours'
                ? 'Automatic reply (after hours)'
                : message.type === 'email_outbound'
                  ? `${message.staffName ?? 'Staff'} (email)`
                  : (message.staffName ?? 'Auto-reply')
            : message.type === 'chat_inbound'
              ? 'Customer (web chat)'
              : message.type === 'contact_form'
                ? 'Customer (contact form)'
                : message.type === 'email_inbound'
                  ? 'Customer (email)'
                  : 'Customer'}{' '}
          · {time}
          {message.type === 'email_outbound' && message.emailStatus === 'queued'
            ? ' · Sending…'
            : null}
        </p>
        {message.type === 'email_outbound' && message.emailStatus === 'failed' ? (
          <p className="mt-0.5 text-2xs text-danger">
            Failed to send{message.emailError ? `: ${message.emailError}` : ''}
          </p>
        ) : null}
      </div>
    </div>
  )
}

/** The frozen transcript of a chat widget conversation's AI phase (t0948),
 *  written once at handoff — unlike `AiReceptionistBubble`, there is nothing
 *  live to poll here, so it renders straight from `message.body`
 *  (`formatChatTranscriptBody` on the API side) rather than a joined call
 *  record. Collapsible the same way a long call transcript is: useful to
 *  staff, but not something that should push the actual conversation below
 *  the fold every time the thread loads. */
function ChatTranscriptBubble({ message, time }: { message: SupportTicketMessage; time: string }) {
  const [expanded, setExpanded] = useState(false)
  const [summary, ...rest] = message.body.split('\n')

  return (
    <div className="flex items-start gap-2 rounded-md border border-dashed border-border bg-surface-sunken px-3 py-2 text-sm">
      <Bot className="mt-0.5 size-3.5 shrink-0 text-foreground-subtle" aria-hidden="true" />
      <div className="min-w-0 flex-1">
        <p className="font-medium">{summary}</p>
        <button
          type="button"
          className="mt-1 text-xs text-primary hover:underline"
          onClick={() => setExpanded((v) => !v)}
        >
          {expanded ? 'Hide transcript' : 'Show transcript'}
        </button>
        {expanded ? (
          <p className="mt-1.5 whitespace-pre-wrap border-t border-border pt-1.5 text-xs">
            <LinkifiedText text={rest.join('\n').trim()} />
          </p>
        ) : null}
        <p className="mt-0.5 text-2xs text-foreground-muted">{time}</p>
      </div>
    </div>
  )
}

const PLAYBACK_SPEEDS = [1, 1.25, 1.5, 2]

/** The audio player for a call or voicemail recording: the browser's own
 *  controls (the timeline drag works because the API answers range requests)
 *  plus 10-second skip buttons and a playback-speed toggle (t1338). */
function RecordingPlayer({ src }: { src: string }) {
  const audioRef = useRef<HTMLAudioElement>(null)
  const [speed, setSpeed] = useState(1)

  function skip(seconds: number) {
    const audio = audioRef.current
    if (!audio) return
    const max = Number.isFinite(audio.duration) ? audio.duration : Number.POSITIVE_INFINITY
    audio.currentTime = Math.min(max, Math.max(0, audio.currentTime + seconds))
  }

  function cycleSpeed() {
    const next = PLAYBACK_SPEEDS[(PLAYBACK_SPEEDS.indexOf(speed) + 1) % PLAYBACK_SPEEDS.length] ?? 1
    setSpeed(next)
    if (audioRef.current) audioRef.current.playbackRate = next
  }

  return (
    <div>
      {/* biome-ignore lint/a11y/useMediaCaption: a phone recording has no captions to offer */}
      <audio ref={audioRef} controls preload="metadata" src={src} className="h-8 w-full" />
      <div className="mt-1 flex items-center gap-1.5">
        <Button
          type="button"
          size="sm"
          variant="secondary"
          aria-label="Back 10 seconds"
          onClick={() => skip(-10)}
        >
          <RotateCcw className="size-3.5" aria-hidden="true" />
          10s
        </Button>
        <Button
          type="button"
          size="sm"
          variant="secondary"
          aria-label="Forward 10 seconds"
          onClick={() => skip(10)}
        >
          10s
          <RotateCw className="size-3.5" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          size="sm"
          variant="ghost"
          aria-label="Playback speed"
          onClick={cycleSpeed}
        >
          {speed}×
        </Button>
      </div>
    </div>
  )
}

/** An answered browser-softphone call (t0886). A missed/failed/unanswered
 *  call has no recording and keeps rendering as the plain one-line row it
 *  always has; only an answered one expands into the voicemail-style card
 *  with playback and, once ready, an expandable transcript. */
function CallBubble({
  ticketId,
  message,
  time,
}: {
  ticketId: string
  message: SupportTicketMessage
  time: string
}) {
  const [expanded, setExpanded] = useState(false)
  const queryClient = useQueryClient()
  const retranscribe = useMutation({
    mutationFn: () =>
      api.post(`/support-tickets/${ticketId}/messages/${message.id}/transcribe`, {}),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() }),
  })
  const redo = () => retranscribe.mutate()
  const outbound = message.type === 'call_outbound'
  const Icon = outbound ? PhoneOutgoing : Phone
  const meta = outbound ? `${message.staffName ?? 'Staff'} · ${time}` : time

  if (!message.hasRecording) {
    return (
      <div className="flex items-center gap-2 text-xs text-foreground-muted">
        <Icon className="size-3.5" aria-hidden="true" />
        {message.body} · {meta}
      </div>
    )
  }

  return (
    <div className="max-w-[80%] rounded-lg border border-border bg-surface px-3 py-2 text-sm">
      <p className="mb-1.5 flex items-center gap-1.5 font-medium">
        <Icon className="size-3.5 shrink-0 text-foreground-subtle" aria-hidden="true" />
        {message.body}
        {message.recordingDurationSeconds ? ` · ${message.recordingDurationSeconds}s` : ''}
      </p>
      <RecordingPlayer
        src={`${apiBaseUrl}/support-tickets/${ticketId}/messages/${message.id}/recording`}
      />
      {message.transcriptStatus === 'pending' ? (
        <p className="mt-1 text-2xs text-foreground-muted">Transcribing…</p>
      ) : null}
      {message.transcriptStatus === 'failed' ? (
        <p className="mt-1 text-2xs text-foreground-muted">
          Transcript unavailable ·{' '}
          <button type="button" className="text-primary hover:underline" onClick={redo}>
            Try again
          </button>
        </p>
      ) : null}
      {message.transcriptStatus === 'completed' && !message.transcriptText ? (
        <p className="mt-1 text-2xs text-foreground-muted">No speech detected</p>
      ) : null}
      {message.transcriptStatus === 'completed' && message.transcriptText ? (
        <>
          <button
            type="button"
            className="mt-1.5 text-xs text-primary hover:underline"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded ? 'Hide transcript' : 'Show transcript'}
          </button>
          {expanded ? (
            <div className="mt-1.5 space-y-1.5 border-t border-border pt-1.5 text-xs">
              {message.transcriptText.split('\n\n').map((turn, i) => {
                const m = /^(Customer|Staff|Speaker [A-Z]): ([\s\S]*)$/.exec(turn)
                return (
                  // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, ordered transcript
                  <p key={i} className="whitespace-pre-wrap">
                    {m ? <span className="font-medium">{m[1]}: </span> : null}
                    <LinkifiedText text={m ? (m[2] ?? '') : turn} />
                  </p>
                )
              })}
              <button
                type="button"
                className="text-2xs text-primary hover:underline"
                onClick={redo}
              >
                Redo transcript
              </button>
            </div>
          ) : null}
        </>
      ) : null}
      <p className="mt-1 text-2xs text-foreground-muted">{meta}</p>
    </div>
  )
}

const CHANNEL_LABEL: Record<string, string> = {
  sms: 'Text',
  call: 'Call',
  manual: 'Manual',
  web_chat: 'Web chat',
  contact_form: 'Contact form',
  email: 'Email',
}

const AI_STATUS_LABEL: Record<string, string> = {
  active: 'Caller disconnected before finishing',
  voicemail: 'Handed off to voicemail',
  resolved: 'Resolved by the assistant',
  abandoned: 'Caller disconnected before finishing',
}

/** What the AI receptionist (t0889) collected on one call — a summary line
 *  plus an expandable transcript, so staff can review it without leaving the
 *  ticket thread. `status: 'active'` here means the call never reached a
 *  terminal state (the caller hung up mid-conversation) rather than that
 *  it's still ringing — nothing calls back into this once the webhook chain
 *  stops. */
function AiReceptionistBubble({ message, time }: { message: SupportTicketMessage; time: string }) {
  const [expanded, setExpanded] = useState(false)
  const call = message.aiCall
  const collected = call?.collected

  return (
    <div className="max-w-[85%] rounded-lg border border-border bg-surface px-3 py-2 text-sm">
      <p className="mb-1 flex items-center gap-1.5 font-medium">
        <Bot className="size-3.5 shrink-0 text-foreground-subtle" aria-hidden="true" />
        AI receptionist
      </p>
      {call ? (
        <>
          <p className="text-xs text-foreground-muted">
            {AI_STATUS_LABEL[call.status] ?? call.status}
          </p>
          <dl className="mt-1.5 space-y-0.5 text-xs">
            <div className="flex gap-1">
              <dt className="text-foreground-subtle">Name:</dt>
              <dd>{collected?.name ?? '—'}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="text-foreground-subtle">Callback:</dt>
              <dd>{collected?.callbackNumber ?? '—'}</dd>
            </div>
            <div className="flex gap-1">
              <dt className="text-foreground-subtle">Reason:</dt>
              <dd>{collected?.reason ?? '—'}</dd>
            </div>
          </dl>
          {call.transcript.length > 0 ? (
            <>
              <button
                type="button"
                className="mt-1.5 text-xs text-primary hover:underline"
                onClick={() => setExpanded((v) => !v)}
              >
                {expanded ? 'Hide transcript' : 'Show transcript'}
              </button>
              {expanded ? (
                <div className="mt-1.5 space-y-1 border-t border-border pt-1.5">
                  {call.transcript.map((turn, i) => (
                    // biome-ignore lint/suspicious/noArrayIndexKey: a fixed, ordered transcript with no id of its own
                    <p key={i} className="text-xs">
                      <span className="font-medium">{turn.role === 'ai' ? 'AI' : 'Caller'}:</span>{' '}
                      {turn.text}
                    </p>
                  ))}
                </div>
              ) : null}
            </>
          ) : null}
        </>
      ) : (
        <p className="whitespace-pre-wrap text-xs text-foreground-muted">
          <LinkifiedText text={message.body} />
        </p>
      )}
      <p className="mt-1 text-2xs text-foreground-muted">{time}</p>
    </div>
  )
}

/** The previous conversation from this number, dimmed above the current
 *  messages (t1230). Shows the last few messages; the rest are one click away. */
function EarlierConversation({ earlier }: { earlier: SupportTicketEarlier }) {
  const [showAll, setShowAll] = useState(false)
  const { previous, others } = earlier
  if (!previous) return null
  const PREVIEW = 5
  const hidden = Math.max(0, previous.messages.length - PREVIEW)
  const visible = showAll ? previous.messages : previous.messages.slice(-PREVIEW)
  const when = new Date(previous.ticket.lastMessageAt).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  })

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-foreground-muted">
        <span>Earlier conversation from this number ·</span>
        <Link
          to="/support-tickets/$ticketId"
          params={{ ticketId: previous.ticket.id }}
          className="font-medium text-foreground hover:underline"
        >
          {previous.ticket.ticketNumber}
        </Link>
        <span>
          · {humanizeStatus(previous.ticket.status)} · {when}
        </span>
        {hidden > 0 && !showAll ? (
          <button
            type="button"
            className="underline hover:text-foreground"
            onClick={() => setShowAll(true)}
          >
            Show all {previous.messages.length} messages
          </button>
        ) : null}
      </div>
      <div className="space-y-2 opacity-70">
        {visible.map((m) => (
          <MessageBubble key={m.id} ticketId={m.ticketId} message={m} />
        ))}
      </div>
      {others.length > 0 ? (
        <p className="text-2xs text-foreground-muted">
          Older tickets from this number:{' '}
          {others.map((t, i) => (
            <span key={t.id}>
              {i > 0 ? ', ' : ''}
              <Link
                to="/support-tickets/$ticketId"
                params={{ ticketId: t.id }}
                className="hover:underline"
              >
                {t.ticketNumber}
              </Link>
            </span>
          ))}
        </p>
      ) : null}
      <div className="flex items-center gap-2 text-2xs uppercase tracking-wide text-foreground-subtle">
        <span className="h-px flex-1 bg-border" />
        This ticket
        <span className="h-px flex-1 bg-border" />
      </div>
    </div>
  )
}

interface CustomerNumber {
  phone: string
  label: string
  smsCapable: boolean
}

/**
 * The linked customer's numbers (t1246) — primary first, then the extras —
 * for a ticket that has a customer but no number of its own. Same query keys
 * as the customer page, so the cache is shared.
 */
function useCustomerNumbers(ticket: SupportTicketDetail): CustomerNumber[] {
  const { can } = useAuth()
  const enabled = Boolean(ticket.customerId) && !ticket.contactPhone && can('customers:read')
  const customerId = ticket.customerId ?? ''

  const { data: customer } = useQuery({
    queryKey: queryKeys.customers.detail(customerId),
    queryFn: () => api.get<{ phone: string | null }>(`/customers/${customerId}`),
    enabled,
  })
  const { data: extras } = useQuery({
    queryKey: queryKeys.customers.phones(customerId),
    queryFn: () =>
      api.get<{ phone: string; label: string | null; smsCapable: boolean }[]>(
        `/customers/${customerId}/phones`,
      ),
    enabled,
  })

  if (!enabled) return []
  const numbers: CustomerNumber[] = []
  // The primary number is the one the customer's texts go to, so it takes texts.
  if (customer?.phone) numbers.push({ phone: customer.phone, label: 'Primary', smsCapable: true })
  for (const extra of extras ?? []) {
    if (numbers.some((n) => n.phone === extra.phone)) continue
    numbers.push({
      phone: extra.phone,
      label: extra.label ?? 'Other',
      smsCapable: extra.smsCapable,
    })
  }
  return numbers
}

/**
 * Puts one of the customer's numbers on the ticket (t1246), so the Call
 * button, text replies, and call/text logging all work on it — the server
 * threads both by `contactPhone`.
 */
function UseCustomerNumber({
  ticket,
  numbers,
  variant = 'secondary',
}: {
  ticket: SupportTicketDetail
  numbers: CustomerNumber[]
  variant?: 'secondary' | 'ghost'
}) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()

  const use = useMutation({
    mutationFn: (phone: string) =>
      api.patch(`/support-tickets/${ticket.id}`, { contactPhone: phone }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success('Number added to the ticket')
    },
    onError: (error) =>
      toast.error(
        'Could not add the number',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  if (!can('support:write') || numbers.length === 0) return null

  const only = numbers.length === 1 ? numbers[0] : undefined
  if (only) {
    return (
      <Button
        size="sm"
        variant={variant}
        disabled={use.isPending}
        onClick={() => use.mutate(only.phone)}
      >
        <Phone className="size-3.5" aria-hidden="true" /> Use {formatUsPhone(only.phone)}
      </Button>
    )
  }
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button size="sm" variant={variant} disabled={use.isPending}>
          <Phone className="size-3.5" aria-hidden="true" /> Use a customer number
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {numbers.map((n) => (
          <DropdownMenuItem key={n.phone} onSelect={() => use.mutate(n.phone)}>
            {formatUsPhone(n.phone)} · {n.label}
            {n.smsCapable ? '' : ' · no texts'}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

function ConversationCard({
  ticket,
  messages,
  earlier,
  canReply,
}: {
  ticket: SupportTicketDetail
  messages: SupportTicketMessage[]
  earlier: SupportTicketEarlier | null
  canReply: boolean
}) {
  const toast = useToast()
  // With earlier history above, open scrolled to the newest message so staff
  // still land on the new text, not the old thread (t1230).
  const threadRef = useRef<HTMLDivElement>(null)
  const hasEarlier = Boolean(earlier?.previous)
  const hasMessages = messages.length > 0
  // biome-ignore lint/correctness/useExhaustiveDependencies: re-run only when the earlier block or the messages first appear
  useEffect(() => {
    if (hasEarlier && hasMessages && threadRef.current) {
      threadRef.current.scrollTop = threadRef.current.scrollHeight
    }
  }, [hasEarlier, hasMessages, ticket.id])
  const queryClient = useQueryClient()
  const [body, setBody] = useState('')
  const [notingMode, setNotingMode] = useState(false)
  const [cannedId, setCannedId] = useState('')
  // Pictures staged for the next text (t1231). They live server-side from the
  // moment they are attached, so Send only has to name them.
  const [pictures, setPictures] = useState<TicketAttachment[]>([])
  const [uploading, setUploading] = useState(false)
  const fileInputRef = useRef<HTMLInputElement>(null)
  // An internal note can carry any file (t1249); texts stay pictures-only above.
  const noteFiles = useFileUploads({
    endpoint: `/support-tickets/${ticket.id}/attachments?purpose=note`,
    deletePath: (id) => `/support-tickets/${ticket.id}/attachments/${id}`,
    normalize: (raw) => ({
      ...raw,
      url: `${apiBaseUrl}/support-tickets/${ticket.id}/attachments/${raw.id}`,
    }),
  })
  // A web chat ticket replies straight into the widget — no phone number is
  // ever required for it, unlike an SMS/call ticket (see `reply()` on the
  // API side, which branches the same way).
  const isWebChat = ticket.channel === 'web_chat'
  // An email ticket is answered by email (t1236) — it needs an address, not a phone number.
  const isEmail = ticket.channel === 'email'
  const customerNumbers = useCustomerNumbers(ticket)
  const replyBlocked =
    !notingMode && !isWebChat && (isEmail ? !ticket.contactEmail : !ticket.contactPhone)

  const { data: canned } = useQuery({
    queryKey: queryKeys.supportCannedResponses(),
    queryFn: () => api.get<CannedResponse[]>('/support-canned-responses'),
    enabled: canReply,
  })

  // The thread so far, for AI reply drafts (t1232). Internal notes are left
  // out so a staff-only remark can never be echoed into a customer message.
  const aiConversation = messages
    .flatMap((m) => {
      const from = (
        ['sms_inbound', 'chat_inbound', 'email_inbound', 'contact_form'].includes(m.type)
          ? 'customer'
          : [
                'sms_outbound',
                'chat_outbound',
                'email_outbound',
                'chat_ai',
                'ai_receptionist',
              ].includes(m.type)
            ? 'staff'
            : null
      ) as 'customer' | 'staff' | null
      const text = (m.body || m.transcriptText || '').trim().slice(0, 4000)
      return from && text ? [{ from, text }] : []
    })
    .slice(-20)

  const applyCanned = (id: string) => {
    setCannedId(id)
    const response = canned?.find((c) => c.id === id)
    if (response) setBody(response.body)
  }

  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })

  const canAttach = !notingMode && !isWebChat && !isEmail && !replyBlocked

  async function uploadFiles(files: File[]) {
    if (files.length === 0) return
    setUploading(true)
    try {
      let count = pictures.length
      for (const file of files) {
        if (count >= MAX_SMS_MEDIA_COUNT) {
          toast.error(
            'Could not attach',
            `A text can carry at most ${MAX_SMS_MEDIA_COUNT} pictures.`,
          )
          break
        }
        if (!(SMS_MEDIA_CONTENT_TYPES as readonly string[]).includes(file.type)) {
          toast.error(
            'Could not attach',
            `${file.name || 'That file'} is not a JPEG, PNG or GIF picture.`,
          )
          continue
        }
        if (file.size > MAX_SMS_MEDIA_BYTES) {
          toast.error('Could not attach', `${file.name || 'That picture'} is larger than 5 MB.`)
          continue
        }
        const formData = new FormData()
        formData.append('file', file)
        const picture = await api.postFile<TicketAttachment>(
          `/support-tickets/${ticket.id}/attachments`,
          formData,
        )
        setPictures((prev) => [...prev, picture])
        count += 1
      }
    } catch (error) {
      toast.error(
        'Could not attach the picture',
        error instanceof ApiError ? error.message : undefined,
      )
    } finally {
      setUploading(false)
    }
  }

  async function removePicture(id: string) {
    try {
      await api.delete(`/support-tickets/${ticket.id}/attachments/${id}`)
      setPictures((prev) => prev.filter((p) => p.id !== id))
    } catch (error) {
      toast.error(
        'Could not remove the picture',
        error instanceof ApiError ? error.message : undefined,
      )
    }
  }

  // Ctrl/Cmd+V with an image on the clipboard attaches it; pasting text is untouched.
  function handlePaste(e: React.ClipboardEvent<HTMLTextAreaElement>) {
    if (!canAttach) return
    const images = Array.from(e.clipboardData.files).filter((f) => f.type.startsWith('image/'))
    if (images.length === 0) return
    e.preventDefault()
    void uploadFiles(
      images.map((f, i) =>
        f.name && f.name !== 'image.png'
          ? f
          : new File([f], `pasted-image-${Date.now()}-${i + 1}.${f.type.split('/')[1] ?? 'png'}`, {
              type: f.type,
            }),
      ),
    )
  }

  const send = useMutation({
    mutationFn: () =>
      notingMode
        ? api.post(`/support-tickets/${ticket.id}/notes`, { body, attachmentIds: noteFiles.ids })
        : api.post(`/support-tickets/${ticket.id}/reply`, {
            body,
            attachmentIds: pictures.map((p) => p.id),
          }),
    onSuccess: async () => {
      await invalidate()
      setBody('')
      if (!notingMode) setPictures([])
      else noteFiles.reset()
      setCannedId('')
      toast.success(
        notingMode ? 'Note added' : isWebChat ? 'Reply sent' : isEmail ? 'Email sent' : 'Text sent',
      )
    },
    onError: (error) =>
      toast.error(
        notingMode ? 'Could not add the note' : 'Could not send the reply',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <MessageSquare className="size-4" aria-hidden="true" /> Conversation
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div ref={threadRef} className="max-h-[28rem] space-y-2 overflow-y-auto scrollbar-thin">
          {earlier ? <EarlierConversation earlier={earlier} /> : null}
          {messages.length === 0 ? (
            <p className="text-sm text-foreground-muted">Nothing here yet.</p>
          ) : (
            messages.map((m) => <MessageBubble key={m.id} ticketId={ticket.id} message={m} />)
          )}
        </div>

        {canReply ? (
          <div className="space-y-2 border-t border-border pt-3">
            <div className="flex items-center gap-2">
              <div className="flex rounded-md border border-border p-0.5">
                <Button
                  size="sm"
                  variant={notingMode ? 'ghost' : 'primary'}
                  onClick={() => setNotingMode(false)}
                >
                  {isWebChat ? 'Reply' : isEmail ? 'Reply by email' : 'Reply by text'}
                </Button>
                <Button
                  size="sm"
                  variant={notingMode ? 'primary' : 'ghost'}
                  onClick={() => setNotingMode(true)}
                >
                  Internal note
                </Button>
              </div>
              {!notingMode && !replyBlocked ? (
                <div className="ms-auto flex items-center">
                  <AiAssistButton
                    target="plain-text"
                    value={body}
                    onApply={setBody}
                    context={{
                      fieldLabel: isWebChat
                        ? 'chat reply'
                        : isEmail
                          ? 'email reply'
                          : 'text message reply',
                      maxLength: isWebChat ? 2000 : isEmail ? 5000 : 300,
                      conversation: aiConversation,
                    }}
                  />
                </div>
              ) : null}
              {!notingMode && canned && canned.length > 0 ? (
                <Select value={cannedId} onValueChange={applyCanned}>
                  <SelectTrigger className={replyBlocked ? 'ms-auto w-48' : 'ms-2 w-48'}>
                    <SelectValue placeholder="Canned response…" />
                  </SelectTrigger>
                  <SelectContent>
                    {canned.map((c) => (
                      <SelectItem key={c.id} value={c.id}>
                        {c.title}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : null}
            </div>

            {replyBlocked ? (
              <Alert tone="info">
                {isEmail
                  ? 'This ticket has no email address, so an email reply cannot be sent.'
                  : customerNumbers.length > 0
                    ? `This ticket has no phone number yet. Use ${ticket.customerName ?? 'the customer'}'s number to text them.`
                    : 'This ticket has no phone number, so a text reply cannot be sent.'}
                {!isEmail ? (
                  <div className="mt-2">
                    <UseCustomerNumber ticket={ticket} numbers={customerNumbers} />
                  </div>
                ) : null}
              </Alert>
            ) : (
              <Textarea
                rows={3}
                value={body}
                onChange={(e) => setBody(e.target.value)}
                onPaste={notingMode ? noteFiles.handlePaste : handlePaste}
                placeholder={
                  notingMode
                    ? 'Only staff see this. The assigned person is notified…'
                    : isWebChat
                      ? 'Message the shopper…'
                      : isEmail
                        ? 'Write an email reply…'
                        : 'Type a reply…'
                }
              />
            )}
            {notingMode ? <AttachmentChips uploads={noteFiles} /> : null}
            {canAttach && pictures.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {pictures.map((p) => (
                  <div key={p.id} className="relative">
                    <img
                      src={`${apiBaseUrl}/support-tickets/${ticket.id}/attachments/${p.id}`}
                      alt={p.filename}
                      className="size-16 rounded-md border border-border object-cover"
                    />
                    <button
                      type="button"
                      aria-label={`Remove ${p.filename}`}
                      className="absolute -end-1.5 -top-1.5 rounded-full border border-border bg-surface p-0.5 text-foreground-muted hover:text-foreground"
                      onClick={() => void removePicture(p.id)}
                    >
                      <X className="size-3" aria-hidden="true" />
                    </button>
                  </div>
                ))}
              </div>
            ) : null}
            {isEmail && !notingMode && !replyBlocked ? (
              <p className="text-xs text-foreground-muted">
                Sent to {ticket.contactEmail} from your support mailbox, in the same email
                conversation.
              </p>
            ) : null}
            {canAttach ? (
              <p className="text-xs text-foreground-muted">
                Texts start with “Mevaser Tech:” and end with “&gt;Reply STOP to opt out.” — both
                are added automatically (every text, or just the first, per Support settings).
                Attach or paste a picture to send it as a picture message (JPEG, PNG or GIF, 5 MB
                total).
              </p>
            ) : null}

            <div className="flex items-center justify-end gap-2">
              {notingMode ? (
                <div className="me-auto">
                  <AttachButton uploads={noteFiles} />
                </div>
              ) : null}
              {canAttach ? (
                <>
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    accept={SMS_MEDIA_CONTENT_TYPES.join(',')}
                    className="hidden"
                    onChange={(e) => {
                      void uploadFiles(Array.from(e.target.files ?? []))
                      e.target.value = ''
                    }}
                  />
                  <Button
                    type="button"
                    variant="ghost"
                    size="sm"
                    className="me-auto"
                    disabled={uploading}
                    onClick={() => fileInputRef.current?.click()}
                  >
                    <Paperclip className="size-4" /> {uploading ? 'Attaching…' : 'Attach'}
                  </Button>
                </>
              ) : null}
              <Button
                variant="primary"
                size="sm"
                disabled={
                  (!body.trim() &&
                    !(canAttach && pictures.length > 0) &&
                    !(notingMode && noteFiles.ids.length > 0)) ||
                  send.isPending ||
                  uploading ||
                  noteFiles.uploading ||
                  replyBlocked
                }
                loading={send.isPending}
                onClick={() => send.mutate()}
              >
                {notingMode ? 'Add note' : 'Send'}
              </Button>
            </div>
          </div>
        ) : null}
      </CardContent>
    </Card>
  )
}

function DetailsCard({ ticket }: { ticket: SupportTicketDetail }) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canWrite = can('support:write')
  const canSeeStaff = can('staff:read')
  const canLinkCustomer = canWrite && can('customers:read')
  const customerNumbers = useCustomerNumbers(ticket)
  const [linkOpen, setLinkOpen] = useState(false)
  const [unlinkOpen, setUnlinkOpen] = useState(false)

  const [assignedStaffId, setAssignedStaffId] = useState(ticket.assignedStaffId ?? UNASSIGNED)

  useEffect(() => {
    setAssignedStaffId(ticket.assignedStaffId ?? UNASSIGNED)
  }, [ticket.assignedStaffId])

  const { data: staff } = useQuery({
    queryKey: queryKeys.staff.list(),
    queryFn: () => api.get<StaffRow[]>('/staff'),
    enabled: canSeeStaff,
  })
  const activeStaff = (staff ?? []).filter((s) => s.isActive)

  const dirty = assignedStaffId !== (ticket.assignedStaffId ?? UNASSIGNED)

  const save = useMutation({
    mutationFn: () =>
      api.patch(`/support-tickets/${ticket.id}`, {
        assignedStaffId: assignedStaffId === UNASSIGNED ? null : assignedStaffId,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success('Assignment updated')
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  const unlink = useMutation({
    mutationFn: () => api.patch(`/support-tickets/${ticket.id}`, { customerId: null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      setUnlinkOpen(false)
      toast.success('Customer unlinked')
    },
    onError: (error) =>
      toast.error('Could not unlink', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Card>
      <CardHeader>
        <CardTitle>Details</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Channel</p>
          <Badge tone={SUPPORT_CHANNEL_TONE[ticket.channel] ?? 'neutral'}>
            {CHANNEL_LABEL[ticket.channel] ?? ticket.channel}
          </Badge>
        </div>
        <div>
          <p className="text-2xs uppercase tracking-wide text-foreground-subtle">Contact</p>
          <p>
            {ticket.customerId ? (
              <Link
                to="/customers/$customerId"
                params={{ customerId: ticket.customerId }}
                className="font-medium hover:underline"
              >
                {ticket.customerName ?? 'Customer'}
              </Link>
            ) : (
              (ticket.customerName ?? 'Unlinked')
            )}
          </p>
          {ticket.contactPhone ? (
            <p className="font-identifier text-xs text-foreground-muted">
              {formatUsPhone(ticket.contactPhone)}
            </p>
          ) : null}
          {ticket.contactEmail ? (
            <p className="text-xs text-foreground-muted">{ticket.contactEmail}</p>
          ) : null}
          {!ticket.contactPhone ? (
            <div className="mt-2">
              <UseCustomerNumber ticket={ticket} numbers={customerNumbers} />
            </div>
          ) : null}
          {!ticket.customerId && canLinkCustomer ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2"
              onClick={() => setLinkOpen(true)}
            >
              <UserPlus className="size-3.5" aria-hidden="true" /> Link customer
            </Button>
          ) : null}
          {ticket.customerId && canLinkCustomer ? (
            <Button
              variant="secondary"
              size="sm"
              className="mt-2 me-2"
              onClick={() => setLinkOpen(true)}
            >
              <ArrowLeftRight className="size-3.5" aria-hidden="true" /> Change customer
            </Button>
          ) : null}
          {ticket.customerId && canWrite ? (
            <Button variant="ghost" size="sm" className="mt-2" onClick={() => setUnlinkOpen(true)}>
              <UserMinus className="size-3.5" aria-hidden="true" /> Unlink customer
            </Button>
          ) : null}
        </div>

        {canSeeStaff ? (
          <Field label="Assigned to">
            {(props) => (
              <Select
                value={assignedStaffId}
                onValueChange={setAssignedStaffId}
                disabled={!canWrite}
              >
                <SelectTrigger {...props}>
                  <SelectValue placeholder="Unassigned" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={UNASSIGNED}>Unassigned</SelectItem>
                  {activeStaff.map((s) => (
                    <SelectItem key={s.id} value={s.id}>
                      {s.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          </Field>
        ) : null}

        {canWrite ? (
          <Button
            variant="primary"
            size="sm"
            disabled={!dirty}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        ) : null}
      </CardContent>
      <LinkCustomerDialog ticket={ticket} open={linkOpen} onOpenChange={setLinkOpen} />
      <Dialog open={unlinkOpen} onOpenChange={setUnlinkOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Unlink {ticket.customerName ?? 'customer'} from this ticket?</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p className="text-sm">
              The ticket keeps its contact details and message history. You can link a customer
              again afterward.
            </p>
          </DialogBody>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setUnlinkOpen(false)}>
              Cancel
            </Button>
            <Button variant="danger" disabled={unlink.isPending} onClick={() => unlink.mutate()}>
              {unlink.isPending ? 'Unlinking…' : 'Unlink'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  )
}

/**
 * Links, changes, or creates the customer for a ticket (t0888, t1122).
 * An exact-number lookup (the same `findUserByAnyPhone` the customer/phone
 * endpoints use for their own conflict checks) is only a suggestion: finding
 * an existing customer and creating a new one are always offered, with or
 * without a phone on the ticket, because the ticket's number can belong to
 * the wrong person (someone chatting from a signed-in shared computer). The
 * ticket's number is attached to a picked customer only when nobody owns it
 * yet. Every path ends the same way: `PATCH /support-tickets/:id { customerId }`.
 */
function LinkCustomerDialog({
  ticket,
  open,
  onOpenChange,
}: {
  ticket: SupportTicketDetail
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { can } = useAuth()
  const toast = useToast()
  const queryClient = useQueryClient()
  const canAddCustomers = can('customers:write')
  const phone = ticket.contactPhone ?? ''

  const [view, setView] = useState<'root' | 'pick' | 'create'>('root')
  const [pickSearch, setPickSearch] = useState('')
  const [createName, setCreateName] = useState('')
  const [createPhone, setCreatePhone] = useState('')
  const [createEmail, setCreateEmail] = useState('')
  const [createError, setCreateError] = useState<string | undefined>()
  const [conflict, setConflict] = useState<{ id: string; name: string } | null>(null)
  const [retryTarget, setRetryTarget] = useState<{ id: string; name: string } | null>(null)

  const reset = () => {
    setView('root')
    setPickSearch('')
    setCreateName('')
    setCreatePhone('')
    setCreateEmail('')
    setCreateError(undefined)
    setConflict(null)
    setRetryTarget(null)
  }

  const matchQuery = useQuery({
    queryKey: queryKeys.customers.match(phone),
    queryFn: () =>
      api.get<{ customer: CustomerMatch | null }>('/customers/match', { query: { phone } }),
    enabled: open && Boolean(phone),
  })

  const { data: pickResults, isFetching: picking } = useQuery({
    queryKey: queryKeys.customers.list({ q: pickSearch }),
    queryFn: () =>
      api.get<{ items: CustomerMatch[] }>('/customers', { query: { q: pickSearch, pageSize: 6 } }),
    enabled: view === 'pick' && pickSearch.trim().length >= 2,
  })

  const link = useMutation({
    mutationFn: (target: { id: string; name: string }) =>
      api.patch(`/support-tickets/${ticket.id}`, { customerId: target.id }).then(() => target),
    onSuccess: async (target) => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success(`Linked to ${target.name}`)
      onOpenChange(false)
      reset()
    },
    onError: (error, target) => {
      setRetryTarget(target)
      toast.error(
        'Could not link the ticket',
        error instanceof ApiError ? error.message : undefined,
      )
    },
  })

  const addToExisting = useMutation({
    mutationFn: async (target: CustomerMatch): Promise<CustomerMatch | null> => {
      try {
        await api.post(`/customers/${target.id}/phones`, {
          phone,
          label: null,
          smsCapable: ticket.channel === 'sms',
        })
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 409) throw error
        const details = error.details as { customerId?: string; customerName?: string } | undefined
        // A different owner — offer to link to them instead of failing flat.
        // No `details` (a bare unique-violation race) or `details.customerId
        // === target.id` ("already this customer's number") both mean the
        // number is already where it needs to be, so fall through to link.
        if (details?.customerId && details.customerId !== target.id) {
          setConflict({ id: details.customerId, name: details.customerName ?? 'another customer' })
          return null
        }
      }
      await queryClient.invalidateQueries({ queryKey: queryKeys.customers.phones(target.id) })
      return target
    },
    onSuccess: (target) => {
      if (target) link.mutate(target)
    },
    onError: (error) =>
      toast.error(
        'Could not add the number',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  const createAndLink = useMutation({
    mutationFn: async (): Promise<{ id: string; name: string } | null> => {
      const parsed = createCustomerSchema.safeParse({
        phone: createPhone.trim() || null,
        name: createName,
        email: createEmail.trim() || null,
      })
      if (!parsed.success) {
        throw new Error(parsed.error.issues[0]?.message ?? 'Check the form and try again')
      }
      try {
        const customer = await api.post<{ id: string; name: string }>('/customers', parsed.data)
        await queryClient.invalidateQueries({ queryKey: queryKeys.customers.all() })
        return customer
      } catch (error) {
        if (!(error instanceof ApiError) || error.status !== 409) throw error
        const details = error.details as { customerId?: string; customerName?: string } | undefined
        if (details?.customerId) {
          setConflict({ id: details.customerId, name: details.customerName ?? 'another customer' })
          return null
        }
        setCreateError(error.message)
        return null
      }
    },
    onSuccess: (customer) => {
      if (customer) link.mutate(customer)
    },
    onError: (error: Error) => toast.error(error.message),
  })

  const linking = link.isPending || addToExisting.isPending || createAndLink.isPending

  const isChange = Boolean(ticket.customerId)
  const match = matchQuery.data?.customer ?? null
  const matchResolved = matchQuery.data !== undefined
  const suggestion = match && match.id !== ticket.customerId ? match : null
  // The number goes onto a picked customer only when nobody owns it yet —
  // moving a number off its owner is never what a "wrong customer" fix wants.
  const numberIsFree = Boolean(phone) && matchResolved && !match
  const attachPhone = numberIsFree && canAddCustomers

  const openCreate = () => {
    setCreatePhone(numberIsFree ? formatUsPhone(phone) : '')
    setCreateEmail(ticket.contactEmail ?? '')
    setView('create')
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        onOpenChange(next)
        if (!next) reset()
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>{isChange ? 'Change customer' : 'Link customer'}</DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {retryTarget ? (
            <Alert tone="danger">
              <div className="space-y-2">
                <p>The ticket could not be linked to {retryTarget.name}.</p>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={link.isPending}
                  onClick={() => link.mutate(retryTarget)}
                >
                  Retry link
                </Button>
              </div>
            </Alert>
          ) : null}

          {conflict ? (
            <Alert tone="warning">
              <div className="space-y-2">
                <p>That number already belongs to {conflict.name}.</p>
                <Button
                  size="sm"
                  variant="secondary"
                  loading={link.isPending}
                  onClick={() => {
                    const target = conflict
                    setConflict(null)
                    link.mutate(target)
                  }}
                >
                  Link to {conflict.name} instead
                </Button>
              </div>
            </Alert>
          ) : null}

          {view === 'root' ? (
            <div className="space-y-4">
              {!phone ? (
                <p className="text-sm text-foreground-muted">
                  This ticket has no phone number on file.
                </p>
              ) : matchQuery.isLoading ? (
                <Skeleton className="h-16 w-full" />
              ) : matchQuery.isError ? (
                <Alert tone="danger">
                  Could not look up this number. You can still search below.
                </Alert>
              ) : suggestion ? (
                <div className="space-y-3">
                  <p className="text-sm">
                    This number belongs to <span className="font-medium">{suggestion.name}</span>.
                  </p>
                  <Button
                    variant="primary"
                    size="sm"
                    loading={link.isPending}
                    onClick={() => link.mutate(suggestion)}
                  >
                    Link ticket to {suggestion.name}
                  </Button>
                </div>
              ) : match ? (
                <p className="text-sm text-foreground-muted">
                  This number belongs to {match.name}, who is already linked.
                </p>
              ) : (
                <p className="text-sm text-foreground-muted">No customer has this number.</p>
              )}
              <div className="space-y-2">
                {suggestion ? (
                  <p className="text-xs text-foreground-subtle">Not the right person?</p>
                ) : null}
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="secondary"
                    size="sm"
                    disabled={linking}
                    onClick={() => setView('pick')}
                  >
                    Find existing customer
                  </Button>
                  {canAddCustomers ? (
                    <Button variant="secondary" size="sm" disabled={linking} onClick={openCreate}>
                      Create new customer
                    </Button>
                  ) : null}
                </div>
                {!canAddCustomers ? (
                  <p className="text-xs text-foreground-subtle">
                    You don't have permission to add customers.
                  </p>
                ) : null}
              </div>
            </div>
          ) : null}

          {view === 'pick' ? (
            <div className="space-y-3">
              <Input
                autoFocus
                value={pickSearch}
                onChange={(e) => setPickSearch(e.target.value)}
                placeholder="Name or phone number…"
                disabled={linking}
              />
              {attachPhone ? (
                <p className="text-xs text-foreground-subtle">
                  The ticket's number will also be added to the customer you choose.
                </p>
              ) : null}
              {picking ? <Skeleton className="h-10 w-full" /> : null}
              {pickResults?.items.length ? (
                <ul className="divide-y divide-border">
                  {pickResults.items.map((row) => (
                    <li key={row.id}>
                      <button
                        type="button"
                        disabled={linking}
                        className="w-full px-1 py-2 text-start text-sm hover:bg-surface-sunken disabled:opacity-50"
                        onClick={() => (attachPhone ? addToExisting.mutate(row) : link.mutate(row))}
                      >
                        <span className="block font-medium">
                          {row.name}
                          {row.id === ticket.customerId ? (
                            <span className="ms-2 text-2xs font-normal text-foreground-subtle">
                              Currently linked
                            </span>
                          ) : null}
                        </span>
                        <span className="block font-identifier text-2xs text-foreground-muted">
                          {row.phone ? formatUsPhone(row.phone) : '—'}
                        </span>
                      </button>
                    </li>
                  ))}
                </ul>
              ) : null}
              <Button variant="ghost" size="sm" disabled={linking} onClick={() => setView('root')}>
                Back
              </Button>
            </div>
          ) : null}

          {view === 'create' ? (
            <div className="space-y-4">
              <Field label="Phone">
                {(props) => (
                  <Input
                    {...props}
                    type="tel"
                    value={createPhone}
                    onChange={(e) => setCreatePhone(e.target.value)}
                    disabled={linking}
                  />
                )}
              </Field>
              <Field label="Name" required>
                {(props) => (
                  <Input
                    {...props}
                    value={createName}
                    onChange={(e) => setCreateName(e.target.value)}
                    disabled={linking}
                  />
                )}
              </Field>
              <Field label="Email" error={createError}>
                {(props) => (
                  <Input
                    {...props}
                    type="email"
                    value={createEmail}
                    onChange={(e) => {
                      setCreateEmail(e.target.value)
                      setCreateError(undefined)
                    }}
                    disabled={linking}
                  />
                )}
              </Field>
              <div className="flex justify-between">
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={linking}
                  onClick={() => setView('root')}
                >
                  Back
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  loading={linking}
                  disabled={!createName.trim()}
                  onClick={() => createAndLink.mutate()}
                >
                  Create &amp; link
                </Button>
              </div>
            </div>
          ) : null}
        </DialogBody>
        {view === 'root' ? (
          <DialogFooter>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
          </DialogFooter>
        ) : null}
      </DialogContent>
    </Dialog>
  )
}

function TransitionDialog({
  ticket,
  to,
  onClose,
  onFollowUp,
}: {
  ticket: SupportTicketDetail
  to: string | null
  onClose: () => void
  onFollowUp: (note: string) => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const { can } = useAuth()
  const [note, setNote] = useState('')
  const [picked, setPicked] = useState<string | null>(null)

  useEffect(() => {
    if (to === null) {
      setNote('')
      setPicked(null)
    }
  }, [to])

  const showQuickNotes = to === 'closed' || to === 'resolved'
  const canFollowUp = to === 'closed' && can('tasks:write')
  const { data: closeNotes } = useQuery({
    queryKey: queryKeys.supportCloseNotes(),
    queryFn: () => api.get<{ notes: string[] }>('/support-close-notes'),
    enabled: showQuickNotes,
    staleTime: 60_000,
  })
  const shopNotes = (closeNotes?.notes ?? []).filter((n) => n !== FOLLOW_UP_NOTE)
  const presets = canFollowUp ? [...shopNotes, FOLLOW_UP_NOTE] : shopNotes
  const followingUp = canFollowUp && picked === FOLLOW_UP_NOTE
  const pick = (preset: string) => {
    if (picked === preset) {
      setPicked(null)
      setNote('')
    } else {
      setPicked(preset)
      setNote(preset)
    }
  }

  const move = useMutation({
    mutationFn: () =>
      api.post(`/support-tickets/${ticket.id}/transition`, { to, note: note.trim() || null }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success(`${ticket.ticketNumber} is now ${humanizeStatus(to ?? '').toLowerCase()}`)
      setNote('')
      onClose()
    },
    onError: (error) =>
      toast.error(
        'Could not update the ticket',
        error instanceof ApiError ? error.message : undefined,
      ),
  })

  return (
    <Dialog open={to !== null} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>
            {to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''} — {ticket.ticketNumber}
          </DialogTitle>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {showQuickNotes ? (
            <div className="flex flex-wrap gap-1.5">
              {presets.map((preset) => (
                <Button
                  key={preset}
                  size="sm"
                  variant={picked === preset ? 'primary' : 'secondary'}
                  aria-pressed={picked === preset}
                  onClick={() => pick(preset)}
                >
                  {preset}
                </Button>
              ))}
            </div>
          ) : null}
          <Field
            label="Note"
            hint={
              followingUp
                ? 'Closes this ticket and adds a follow-up task to it.'
                : 'Kept on the ticket history.'
            }
          >
            {(props) => (
              <Textarea
                {...props}
                value={note}
                onChange={(e) => setNote(e.target.value)}
                rows={2}
              />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          {followingUp ? (
            <Button variant="primary" onClick={() => onFollowUp(note.trim() || FOLLOW_UP_NOTE)}>
              Close & add follow-up
            </Button>
          ) : (
            <Button variant="primary" onClick={() => move.mutate()} disabled={move.isPending}>
              {move.isPending ? 'Working…' : to ? (ACTION_LABEL[to] ?? humanizeStatus(to)) : ''}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EndChatDialog({
  ticket,
  open,
  onClose,
}: {
  ticket: SupportTicketDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()

  const endChat = useMutation({
    mutationFn: () => api.post(`/support-tickets/${ticket.id}/end-chat`),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success('Chat ended')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not end the chat', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>End this chat?</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <p className="text-sm">
            The shopper will still be able to read the conversation, but can't send more messages.
            The ticket stays open, and they can start a new chat.
          </p>
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="danger" disabled={endChat.isPending} onClick={() => endChat.mutate()}>
            {endChat.isPending ? 'Working…' : 'End chat'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

function EditSubjectDialog({
  ticket,
  open,
  onClose,
}: {
  ticket: SupportTicketDetail
  open: boolean
  onClose: () => void
}) {
  const toast = useToast()
  const queryClient = useQueryClient()
  const [subject, setSubject] = useState(ticket.subject)

  useEffect(() => {
    if (open) setSubject(ticket.subject)
  }, [open, ticket.subject])

  const save = useMutation({
    mutationFn: () => api.patch(`/support-tickets/${ticket.id}`, { subject: subject.trim() }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.supportTickets.all() })
      toast.success('Ticket updated')
      onClose()
    },
    onError: (error) =>
      toast.error('Could not save', error instanceof ApiError ? error.message : undefined),
  })

  return (
    <Dialog open={open} onOpenChange={(next) => !next && onClose()}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Edit subject</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Field label="Subject" required>
            {(props) => (
              <Input {...props} value={subject} onChange={(e) => setSubject(e.target.value)} />
            )}
          </Field>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            disabled={!subject.trim()}
            loading={save.isPending}
            onClick={() => save.mutate()}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
